/**
 * Events emitted by dialogs:
 * - 'open' CustomEvent with detail pointing on the element that triggered dialog opening
 * - 'close' standard Event, `@see` https://developer.mozilla.org/en-US/docs/Web/API/HTMLDialogElement/close_event
 * - 'cancel' standard Event, `@see` https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/cancel_event
 *
 * Attributes:
 * - data-dismissible: Controls both ESC key and backdrop click dismissal. Set by the `dismissible` prop. 'true' allows dismissal, 'false' blocks it. Default: 'true'.
 * - data-deferred-modal: Marks dialogs to auto-open via showModal() on page load. Set by <x-ui.dialog open>.
 *
 * Command Attributes (Invokers pattern):
 * - command="show-modal" commandfor="dialogId": Opens the specified dialog as a modal
 * - command="close": Closes the nearest parent dialog
 *
 * Deferred Modal Pattern (Auto-open dialogs):
 * When <x-ui.dialog open> is used in Blade, the dialog auto-opens on page load via showModal().
 *
 * Why not use native `open` attribute? Because `<dialog open>` creates a NON-MODAL dialog:
 * - No backdrop, no focus trap, no ESC key handling, doesn't block the page.
 * We always want MODAL dialogs, which require calling showModal().
 *
 * Flow:
 * 1. Blade sees `open` prop → adds `data-deferred-modal` (but NOT the native `open` attr)
 * 2. JS finds `dialog[data-deferred-modal]` and calls showModal() for proper modal behavior
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/dialog
 * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLDialogElement
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Invoker_Commands_API (Invokers pattern)
 * @see resources/views/components/ui/dialog.blade.php - Blade component that sets up deferred modals
 *
 * ⚠️ WARNING: Do Not Manipulate the `open` Attribute Directly
 * Never use `dialog.open = false` or `dialog.removeAttribute('open')` to close dialogs.
 * While the `open` property is technically not read-only, directly manipulating it
 * breaks normal dialog interactions:
 * - The `close` event won't fire
 * - Subsequent calls to `close()` and `requestClose()` will have no effect
 *
 * Always use the proper HTMLDialogElement methods:
 * - `showModal()` - opens as modal with backdrop
 * - `close()` - closes the dialog
 * - `requestClose()` - requests closure (can be cancelled)
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLDialogElement/open
 */
import {escapeHtml} from '../utils/markupHelpers.js';
import {getSvgIconTemplate} from './svgIconBuilder.js';
import {t} from '../localization/translate.js';

/**
 * Creates dialog based on title and fields
 * @param {string} dialogTitle plain text
 * @param {string|null} dialogDescription trusted HTML
 * @param {object} dialogFields field properties; labels are plain text, helpText is trusted HTML
 * @returns {HTMLDialogElement} dialog element
 */
function createMinimalDialogWithFields(dialogTitle, dialogDescription, dialogFields) {
    const dialog = document.createElement('dialog');
    dialog.id = `minimalDialog-${Date.now()}`;
    dialog.className = 'dialog fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 border fg-border-neutral-default rounded-lg shadow-lg max-h-[90vh] overflow-y-auto fg-surface-neutral-default p-6 min-w-320';
    const dialogDescriptionHtml = dialogDescription ? `<p>${dialogDescription}</p>` : '';
    dialog.innerHTML = `
${closeDialogButtonInnerHtml(dialog.id)}
<form method="DIALOG" class="flex flex-col gap-4 mt-4">
    <h2 class="dialog__title js-dialogTitle">${escapeHtml(dialogTitle)}</h2>
    ${dialogDescriptionHtml}
    <fieldset class="fieldset">
    </fieldset>
    <footer class="flex justify-end gap-2 mt-4">
        <menu class="flex gap-2">
            <button type="submit" class="px-4 py-2 bg-blue-600 fg-text-neutral-fixed-default-inverse fg-rounded-md hover:bg-blue-700">${escapeHtml(t('OK'))}</button>
            <button type="reset" class="px-4 py-2 border fg-border-neutral-default fg-rounded-md hover:bg-gray-50">${escapeHtml(t('Cancel'))}</button>
        </menu>
    </footer>
</form>
    `;

    const fieldset = dialog.querySelector('fieldset');
    Object.keys(dialogFields).forEach((name) => {
        const config = dialogFields[name];

        if (!Array.isArray(config)) {
            return;
        }

        const wrapper = document.createElement('div');
        wrapper.className = 'mb-4';
        const element = document.createElement(config[0]);
        const properties = config[1];
        const labelText = properties.label || properties.placeholder || '';

        if (labelText && labelText === properties.placeholder) {
            delete properties.placeholder;
        }

        const helpText = properties.helpText;

        // Handle options first for select elements (value must be set after options are added)
        if (properties.options) {
            [...properties.options].forEach((option) => {
                if (option instanceof HTMLElement) {
                    element.options.add(option.cloneNode(true));
                } else {
                    // Handle plain object {value, label}
                    const optionElement = document.createElement('option');
                    optionElement.value = option.value;
                    optionElement.textContent = option.label || option.value;
                    element.options.add(optionElement);
                }
            });
        }

        Object.keys(properties).forEach((propertyName) => {
            if (propertyName === 'helpText' || propertyName === 'options') {
                // Skip - handled separately
                return;
            }
            if (propertyName === 'class') {
                const classes = properties[propertyName].split(' ');
                element.classList.add(classes);
                return;
            }
            try {
                element[propertyName] = properties[propertyName];
            } catch (error) {
                element.setAttribute(propertyName, properties[propertyName]);
            }
        });
        element.name = name;

        if (element.matches('input:not([type="hidden"]), select, textarea') && labelText) {
            const label = document.createElement('label');
            label.className = 'block mb-2 ';
            label.insertAdjacentHTML('afterbegin', `<span class="block mb-1">${escapeHtml(labelText)}</span>`);
            element.className = 'w-full px-3 py-2 border fg-border-neutral-default rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
            label.append(element);
            wrapper.append(label);
        } else {
            wrapper.append(element);
        }

        // Add help text if provided
        if (helpText) {
            const helpElement = document.createElement('small');
            helpElement.className = 'form-text text-muted';
            helpElement.innerHTML = helpText;
            wrapper.append(helpElement);
        }

        fieldset.append(wrapper);
    });

    return dialog;
}

/**
 * Gets and validates dialog element
 * @param {HTMLDialogElement|string} dialog ID or HTML element
 * @returns {HTMLDialogElement} dialog element
 */
function getValidDialog(dialog) {
    let dialogElement = dialog;
    if (typeof dialog === 'string') {
        dialogElement = document.getElementById(dialog);
    }
    if (!dialogElement) {
        throw new Error(`Dialog with id="${dialogElement?.id || dialog}" doesn't exist`);
    }
    if (!dialogElement.matches('dialog')) {
        throw new Error(`Element with id="${dialogElement?.id || dialog}" is not a dialog`);
    }
    return dialogElement;
}

/**
 * Updates values dynamically for dialog elements that can't be set in views
 * @param {HTMLDialogElement} dialogElement
 */
function updateA11yAttributes(dialogElement) {
    // If not set yet, update `aria-labelledby` attribute to point on ID of dialog's title element.
    if (!dialogElement.getAttribute('aria-labelledby')) {
        const titleElement = dialogElement.querySelector('.js-dialogTitle');

        if (titleElement) {
            const titleId = `dialogTitle_${Date.now()}`;

            titleElement.setAttribute('id', titleId);
            dialogElement.setAttribute('aria-labelledby', titleId);
        }
    }
}

const FOCUSABLE_SELECTORS = [
    'a[href]',
    'area[href]',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'button:not([disabled])',
    'iframe',
    'object',
    'embed',
    '[contenteditable]:not([contenteditable="false"])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Returns focusable elements inside the dialog that are visible.
 * @param {HTMLDialogElement} dialogElement
 * @returns {HTMLElement[]}
 */
function getFocusableElements(dialogElement) {
    return [...dialogElement.querySelectorAll(FOCUSABLE_SELECTORS)].filter((element) => {
        const isHiddenInput = element instanceof HTMLInputElement && element.type === 'hidden';
        const isDisabled = element.hasAttribute('disabled') || element.getAttribute('aria-hidden') === 'true';
        const isVisible = element.offsetWidth > 0 || element.offsetHeight > 0 || element.getClientRects().length > 0;

        return !isHiddenInput && !isDisabled && isVisible;
    });
}

/**
 * Trap focus within a dialog element while it is open.
 * @param {HTMLDialogElement} dialogElement
 * @returns {() => void} function to release the trap
 */
function activateFocusTrap(dialogElement) {
    const handleKeyDown = (event) => {
        if (event.key !== 'Tab' || event.defaultPrevented) {
            return;
        }

        const focusableElements = getFocusableElements(dialogElement);

        if (focusableElements.length === 0) {
            event.preventDefault();
            dialogElement.focus();
            return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements.at(-1);
        const currentActiveElement = document.activeElement;
        const isShiftPressed = event.shiftKey;

        // Force focus into the dialog if the active element is outside.
        if (!dialogElement.contains(currentActiveElement)) {
            event.preventDefault();
            firstElement.focus();
            return;
        }

        if (!isShiftPressed && currentActiveElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
            return;
        }

        if (isShiftPressed && (currentActiveElement === firstElement || currentActiveElement === dialogElement)) {
            event.preventDefault();
            lastElement.focus();
        }
    };

    dialogElement.addEventListener('keydown', handleKeyDown, true);

    // Cleanup function: call this when the dialog closes to deactivate the focus trap.
    return () => {
        dialogElement.removeEventListener('keydown', handleKeyDown, true);
    };
}

/**
 * Trigger modal dialog to be displayed.
 * Note, <dialog open> displays NON-MODAL dialog without backdrop.
 * That's why we need to emulate user click to display MODAL dialog.
 * @param {HTMLDialogElement|string} dialog ID or HTML element
 */
export function showDialog(dialog) {
    const dialogElement = getValidDialog(dialog);

    // We create a temporary element with command/commandfor attributes to trigger
    // dialog init listener that could be declared in a different module
    const fakeElement = document.createElement('button');
    fakeElement.setAttribute('command', 'show-modal');
    fakeElement.setAttribute('commandfor', dialogElement.id);
    document.body.append(fakeElement);
    fakeElement.click();
    fakeElement.remove();
}

/**
 * Opens a dialog as a modal with all necessary setup (a11y, focus trap, backdrop, close handling).
 * @param {HTMLDialogElement} dialogElement - The dialog to open
 * @param {HTMLElement} triggerElement - The element that triggered the dialog opening
 */
function openDialogModal(dialogElement, triggerElement) {
    const openEvent = new CustomEvent('open', {detail: triggerElement, cancelable: true});

    if (!dialogElement.dispatchEvent(openEvent)) {
        return;
    }

    updateA11yAttributes(dialogElement);

    // Clean up deferred-modal marker
    dialogElement.removeAttribute('data-deferred-modal');

    // Defensive: close first if already open to avoid InvalidStateError from showModal()
    if (dialogElement.open) {
        dialogElement.close();
    }

    // Show the modal dialog
    dialogElement.showModal();

    // Default to focusing the dialog itself so the close button (typically the first
    // tabbable element) doesn't grab a focus ring on open. Tab/Shift+Tab keep working
    // via the focus trap; pages can opt a specific element in with [autofocus].
    const autofocusElement = dialogElement.querySelector('[autofocus]');
    if (autofocusElement) {
        autofocusElement.focus();
    } else {
        if (!dialogElement.hasAttribute('tabindex')) {
            dialogElement.setAttribute('tabindex', '-1');
        }
        dialogElement.focus();
    }

    // Don't let user scroll the page
    document.body.classList.add('overflow-hidden');

    const handleClickOutside = (clickEvent) => {
        if (clickEvent.target === dialogElement && dialogElement.dataset.dismissible === 'true') {
            const rect = dialogElement.getBoundingClientRect();
            const isInsideDialog = clickEvent.clientX >= rect.left
                && clickEvent.clientX <= rect.right
                && clickEvent.clientY >= rect.top
                && clickEvent.clientY <= rect.bottom;

            if (!isInsideDialog) {
                dialogElement.close();
            }
        }
    };

    dialogElement.addEventListener('click', handleClickOutside);

    // Returns a cleanup function that removes the focus trap's event listeners when called.
    const releaseFocusTrap = activateFocusTrap(dialogElement);

    dialogElement.addEventListener('close', () => {
        releaseFocusTrap();
        dialogElement.removeEventListener('click', handleClickOutside);
        document.body.classList.remove('overflow-hidden');

        if (triggerElement?.isConnected && typeof triggerElement.focus === 'function') {
            triggerElement.focus();
        }
    }, {once: true});
}

/**
 * Handles command attribute clicks for dialog control.
 * Supports: show-modal, close
 * @param {MouseEvent} event
 * @returns {boolean} true if a command was handled
 */
function handleDialogCommand(event) {
    // A handler closer to the element already cancelled this click.
    if (event.defaultPrevented) {
        return false;
    }

    const target = event.target;
    const commandElement = target.closest('[command]');

    if (!commandElement) {
        return false;
    }

    const command = commandElement.getAttribute('command');
    const commandFor = commandElement.getAttribute('commandfor');

    if (command === 'show-modal') {
        if (!commandFor) {
            console.warn('command="show-modal" requires a commandfor attribute');
            return false;
        }

        event.preventDefault();

        const parentDialog = target.closest('dialog');
        if (parentDialog instanceof HTMLDialogElement) {
            parentDialog.close();
        }

        const dialogElement = getValidDialog(commandFor);
        openDialogModal(dialogElement, commandElement);

        return true;
    }

    if (command === 'close') {
        event.preventDefault();
        const parentDialog = target.closest('dialog');

        if (parentDialog instanceof HTMLDialogElement) {
            parentDialog.close();
        }

        return true;
    }

    return false;
}

/**
 * Sets up global dialog support including:
 * - data-dialog attribute for opening dialogs
 * - command="show-modal"/command="close" attributes (Invokers pattern)
 * - ESC key and backdrop click handling
 * - Deferred modal conversion for dialogs with 'open' attribute
 */
export function setupDialogSupport() {
    // capture mouse click on document, detect clicked controls
    document.addEventListener('click', (event) => {
        if (event.target.closest('.plyr')) {
            // Check if the click event was triggered inside the player
            // If so, then ignore this event
            // Because it interferes with plyr functionality
            // Bug: https://github.com/InteractionDesignFoundation/IxDF-web/issues/24473
            return;
        }

        // Handle command attributes first (Invokers pattern)
        if (handleDialogCommand(event)) {
            return;
        }

        const target = event.target;
        const parentDialog = target.closest('dialog');
        const activationElement = target.closest('[data-dialog]');
        if (activationElement) {
            // if another dialog is open, first close it
            if (parentDialog instanceof HTMLDialogElement) {
                parentDialog.close();
            }
            event.preventDefault();
            const dialogElement = getValidDialog(activationElement.dataset.dialog);
            openDialogModal(dialogElement, activationElement);
        } else if (parentDialog && (target.closest(':link') || target.closest('[type="reset"]'))) {
            // Elements handled in-place by HTMX (e.g. boosted pagination inside a modal) swap content
            // without navigating away, so they must not close the dialog. Cover every HTMX request
            // verb and the data-hx-* aliases, while still letting an explicitly disabled boost close.
            const htmxHandledSelector = [
                '[hx-get]', '[hx-post]', '[hx-put]', '[hx-delete]', '[hx-patch]',
                '[hx-boost]:not([hx-boost="false"])',
                '[data-hx-get]', '[data-hx-post]', '[data-hx-put]', '[data-hx-delete]', '[data-hx-patch]',
                '[data-hx-boost]:not([data-hx-boost="false"])',
            ].join(', ');
            if (target.closest(htmxHandledSelector)) {
                return;
            }
            // A link that opens in another tab or downloads a file leaves this page in place, so the dialog must stay open.
            const link = target.closest(':link');
            if (link?.getAttribute('target')?.toLowerCase() === '_blank' || link?.hasAttribute('download')) {
                return;
            }
            parentDialog.close();
        }
    });
    // Handle ESC key for dialogs based on dismissible prop
    // We need to handle both keydown AND cancel events because:
    // 1. keydown catches ESC before form/dialog processing
    // 2. cancel is the standard dialog close event we also need to block
    document.addEventListener('keydown', (event) => {
        // Only handle ESC key
        if (event.key !== 'Escape') {
            return;
        }
        // Find the currently open dialog
        const openDialog = document.querySelector('dialog[open]');
        if (!openDialog) {
            return;
        }
        // Block ESC key if dialog is not dismissible
        if (openDialog.dataset.dismissible === 'false') {
            event.preventDefault();
            event.stopPropagation();
        }
    }, true);

    document.addEventListener('cancel', (event) => {
        const dialogElement = event.target;
        // Only handle cancel events from dialog elements
        if (!dialogElement || !dialogElement.matches('dialog')) {
            return;
        }
        // Block cancel event if dialog is not dismissible
        if (dialogElement.dataset.dismissible === 'false') {
            event.preventDefault();
            event.stopPropagation();
        }
    }, true);

    // Auto-open dialogs marked with 'data-deferred-modal' (set by <x-ui.dialog open> in Blade)
    const deferredDialogs = [...document.querySelectorAll('dialog[data-deferred-modal]')];

    if (deferredDialogs.length > 0) {
        const showNextDialog = () => {
            const dialogElement = deferredDialogs.shift();
            if (!dialogElement) {
                return;
            }

            openDialogModal(dialogElement, dialogElement);

            // When this dialog closes, show the next one
            dialogElement.addEventListener('close', showNextDialog, {once: true});
        };

        showNextDialog();
    }
}

/**
 * Displays modal dialog and returns result
 * @param {HTMLDialogElement|string} dialog ID or HTML element
 * @returns {Promise<string>} clicked dialog action
 */
export async function requestDialog(dialog) {
    return new Promise((resolve) => {
        const dialogElement = getValidDialog(dialog);

        dialogElement.addEventListener('close', () => {
            resolve(dialogElement.returnValue);
        }, {once: true});

        showDialog(dialogElement);
    });
}

/**
 * Resolves the close button of a dialog this module built.
 * @param {HTMLDialogElement} dialogElement
 * @returns {HTMLButtonElement} the close button
 * @throws {Error} when the button is absent. Every caller wires the cancel path to it, so
 * a missing button would leave the dialog awaiting a promise that never settles.
 */
export function getCloseButton(dialogElement) {
    const button = dialogElement.querySelector('.js-dialogButtonClose');
    if (!(button instanceof HTMLButtonElement)) {
        throw new Error(`Dialog "${dialogElement.id}" has no .js-dialogButtonClose button.`);
    }

    return button;
}

/**
 * The button closes the dialog through an invoker command rather than through the
 * delegated `[type="reset"]` click handler in setupDialogSupport(). Nova embeds the RTE
 * without the application bundle, so that handler is not registered there and the button
 * did nothing. `command` needs no JavaScript at all.
 * @param {string} dialogId id of the dialog the button closes
 * @returns {string}
 */
function closeDialogButtonInnerHtml(dialogId) {
    return `
        <button type="button" command="close" commandfor="${dialogId}" aria-label="${escapeHtml(t('Close dialog'))}" class="js-dialogButtonClose absolute button top-2 right-2 p-2 text-gray-400 hover:text-gray-500 fg-rounded-full hover:fg-surface-neutral-muted">
           ${getSvgIconTemplate('close', {size: 'title-lg'})}
        </button>`;
}

/**
 * @todo this is an experimental API that can be unused. If you found it unused in 2025, please remove it.
 * @param {string} innerHtml HTML content of the dialog (as a rule, a <form> element with a content inside)
 * @param {object=} attributes HTML attributes of the dialog.
 * @param {boolean=} withCloseButton Whether to add a close button to the dialog
 * @returns {HTMLDialogElement} HTMLDialogElement detached from the document without any event listeners
 */
function createDynamicDialogElementUsingInnerHtml(innerHtml, attributes, withCloseButton) {
    const dialog = document.createElement('dialog');
    dialog.id = attributes.id || `dynamicDialog-${Date.now()}`;
    dialog.className = attributes.class || 'dialog fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 border fg-border-neutral-default rounded-lg shadow-lg max-h-[90vh] overflow-y-auto fg-surface-neutral-default p-6 min-w-320';

    if (withCloseButton) {
        dialog.innerHTML += closeDialogButtonInnerHtml(dialog.id);
    }

    dialog.innerHTML += innerHtml;

    return dialog;
}

/**
 * Create a dialog element and add it to the document with required event listeners.
 * @param {string} innerHtml HTML content of the dialog (as a rule, a <form> element with a content inside)
 * @param {object=} attributes HTML attributes of the dialog.
 * @param {boolean=} withCloseButton Whether to add a close button to the dialog
 * @returns {HTMLDialogElement} HTMLDialogElement detached from the document without any event listeners
 */
export function createDynamicDialog(innerHtml, attributes = {}, withCloseButton = true) {
    const dialog = createDynamicDialogElementUsingInnerHtml(innerHtml, attributes, withCloseButton);

    // add the created element to the current document
    document.body.append(dialog);

    // remove dialog when it's closed (because it's dynamic)
    dialog.addEventListener('close', () => {
        dialog.remove();
    });

    return dialog;
}

/**
 * Creates and displays <dialog> element based on title and fields
 * @param {string} title plain-text dialog title
 * @param {string|null} dialogDescription trusted HTML dialog description
 * @param {object} fields field properties; labels are plain text, helpText is trusted HTML
 * @returns {Promise<object>} resolved form data
 */
export async function createAndOpenMinimalDialog(title, dialogDescription, fields) {
    return new Promise((callback) => {
        const dialogElement = createMinimalDialogWithFields(title, dialogDescription, fields);
        // add the created element to the current document
        document.body.append(dialogElement);
        // show dialog
        showDialog(dialogElement);
        // submit event triggered when validation passed
        dialogElement.addEventListener('submit', (event) => {
            const data = {};
            new FormData(event.target).forEach((value, key) => {
                data[key] = value;
            });
            callback(data);
        });
        // Cancel. Closing on reset lives in setupDialogSupport(), which only the
        // application bundle calls, so in Nova the dialog would resolve as cancelled and
        // then stay on screen blocking the page. Close it here instead.
        dialogElement.addEventListener('reset', () => {
            callback();
            dialogElement.close();
            dialogElement.remove();
        });
        // The close button closes the dialog through an invoker command, which fires
        // neither a reset nor a close event, so neither listener around this one sees it.
        // Own that cancel here: resolve so the caller stops awaiting, and drop the
        // element. Resolving twice is a no-op once the dialog was submitted.
        getCloseButton(dialogElement).addEventListener('click', () => {
            callback();
            dialogElement.close();
            dialogElement.remove();
        });
        dialogElement.addEventListener('close', () => {
            dialogElement.remove();
        });
    });
}
